import {
  MarketMeta,
  MarketChainUidFromString,
  ShareTokenMeta,
  TokenUidFromString,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { KvClientError, KvService } from "./types";
import { Schema } from "@effect/schema";
import {
  getMarketMetaKey,
  getMarketProtocolKeyMapKey,
  getShareTokenMetaMapKey,
} from "./keys";
import { TokenMap } from "@silo/utils";

export type UpdateMarketMeta = ReturnType<typeof updateMarketMeta>;
export type UpdateMarketMetaSuccess = Effect.Effect.Success<UpdateMarketMeta>;
export type UpdateMarketMetaError = Effect.Effect.Error<UpdateMarketMeta>;
export type UpdateMarketMetaContext = Effect.Effect.Context<UpdateMarketMeta>;

/**
 * TODO: potentially set up `@silo/lua` package
 */
const updateMarketMetaScript = `
  local marketMetaKey = KEYS[1]
  local marketProtocolKeyMapKey = KEYS[2]

  local marketMeta = ARGV[1]
  local marketChainUid = ARGV[2]
  local marketProtocol = ARGV[3]

  redis.call('JSON.SET', marketMetaKey, "$", marketMeta)
  redis.call('HSET', marketProtocolKeyMapKey, marketChainUid, marketProtocol)

  return redis.status_reply("OK")
`;

export function updateMarketMeta(marketMetaArray: MarketMeta[]) {
  return Effect.gen(function* () {
    const kv = yield* KvService;

    const sha = yield* Effect.tryPromise({
      try: () => kv.scriptLoad(updateMarketMetaScript),
      catch: (cause) => new KvClientError({ cause }),
    });

    const markets = yield* Effect.all(
      marketMetaArray.map((decoded) =>
        pipe(
          decoded,
          Schema.encode(MarketMeta),
          Effect.map((encoded) => ({ decoded, encoded }))
        )
      ),
      { concurrency: "unbounded" }
    );

    const shareTokenMetaMap = getShareTokenMetaMap(marketMetaArray);

    const marketProtocolKeyMapKey = getMarketProtocolKeyMapKey();
    const shareTokenMetaMapKey = getShareTokenMetaMapKey();

    yield* Effect.tryPromise({
      try: () => {
        const pipeline = kv.pipeline();

        for (const { decoded, encoded } of markets) {
          const marketMetaKey = getMarketMetaKey(
            decoded.protocolKey,
            decoded.marketAddress
          );

          const marketChainUid = pipe(
            decoded,
            Schema.encodeSync(MarketChainUidFromString)
          );

          pipeline.evalsha(
            sha,
            [marketMetaKey, marketProtocolKeyMapKey],
            [JSON.stringify(encoded), marketChainUid, decoded.protocolKey]
          );
        }

        const hash: Record<string, string> = {};

        for (const [tokenUid, shareTokenMeta] of shareTokenMetaMap.entries()) {
          const tokenUidString = pipe(
            tokenUid,
            Schema.encodeSync(TokenUidFromString)
          );

          const encoded = pipe(
            shareTokenMeta,
            Schema.encodeSync(ShareTokenMeta),
            JSON.stringify
          );

          hash[tokenUidString] = encoded;
        }

        pipeline.hset(shareTokenMetaMapKey, hash);

        return pipeline.exec();
      },
      catch: (cause) => new KvClientError({ cause }),
    });
  });
}

function getShareTokenMetaMap(
  marketMetaArray: MarketMeta[]
): TokenMap<ShareTokenMeta> {
  const shareTokenMetaMap: TokenMap<ShareTokenMeta> = new TokenMap();

  for (const market of marketMetaArray) {
    const {
      baseAsset,
      bridgeAssets: [firstBridgeAsset, secondBridgeAsset],
      protocolKey,
      chainKey,
      marketAddress,
    } = market;

    const assets = [baseAsset, firstBridgeAsset];

    if (secondBridgeAsset) assets.push(secondBridgeAsset);

    for (const asset of assets) {
      const {
        tokenAddress,
        collateralShareToken,
        protectedShareToken,
        debtShareToken,
      } = asset;

      shareTokenMetaMap.set(
        { chainKey, tokenAddress: collateralShareToken },
        {
          protocolKey,
          chainKey,
          marketAddress,
          tokenAddress,
          shareTokenAddress: collateralShareToken,
          positionType: "collateral",
        }
      );

      shareTokenMetaMap.set(
        { chainKey, tokenAddress: protectedShareToken },
        {
          protocolKey,
          chainKey,
          marketAddress,
          tokenAddress,
          shareTokenAddress: protectedShareToken,
          positionType: "protected",
        }
      );

      shareTokenMetaMap.set(
        { chainKey, tokenAddress: debtShareToken },
        {
          protocolKey,
          chainKey,
          marketAddress,
          tokenAddress,
          shareTokenAddress: debtShareToken,
          positionType: "debt",
        }
      );
    }
  }

  return shareTokenMetaMap;
}
